import { WaveFormInput } from '@/generated/graphql';

export const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

export const formatTimeMs = (ms: number) => formatTime(ms / 1000);

let waveformAudioBackendAvailable = true;
let waveformAudioContext: AudioContext | null = null;
let waveformAudioContextIdleTimer: ReturnType<typeof setTimeout> | null = null;
const WAVEFORM_AUDIO_CONTEXT_IDLE_MS = 30_000;

const clearWaveformAudioContextIdleTimer = (): void => {
  if (waveformAudioContextIdleTimer) {
    clearTimeout(waveformAudioContextIdleTimer);
    waveformAudioContextIdleTimer = null;
  }
};

const scheduleWaveformAudioContextClose = (): void => {
  clearWaveformAudioContextIdleTimer();
  waveformAudioContextIdleTimer = setTimeout(() => {
    const contextToClose = waveformAudioContext;
    waveformAudioContext = null;
    if (contextToClose && contextToClose.state !== 'closed') {
      void contextToClose.close().catch(() => undefined);
    }
  }, WAVEFORM_AUDIO_CONTEXT_IDLE_MS);
};

const getAudioContextCtor = (): typeof AudioContext | null => {
  return (
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext ||
    null
  );
};

const getOrCreateWaveformAudioContext = (): AudioContext | null => {
  if (!waveformAudioBackendAvailable) {
    return null;
  }

  if (waveformAudioContext && waveformAudioContext.state !== 'closed') {
    return waveformAudioContext;
  }

  const AudioContextCtor = getAudioContextCtor();
  if (!AudioContextCtor) {
    waveformAudioBackendAvailable = false;
    return null;
  }

  try {
    waveformAudioContext = new AudioContextCtor();
    return waveformAudioContext;
  } catch (error) {
    waveformAudioBackendAvailable = false;
    console.warn(
      '[getWaveformData] Audio backend unavailable. Upload will continue without waveform data.',
      { error }
    );
    return null;
  }
};

// Create min/max peak data set for a single given channel
const calcPeaks = (channelData: Float32Array, peakCount: number): number[] => {
  const blockSize = Math.floor(channelData.length / peakCount);
  const peaks: number[] = [];

  for (let i = 0; i < peakCount; i++) {
    const start = i * blockSize;
    const end = start + blockSize;

    let min = 1.0;
    let max = -1.0;

    for (let j = start; j < end; j++) {
      const val = channelData[j];
      if (val < min) min = val;
      if (val > max) max = val;
    }
    peaks.push(min, max);
  }
  return peaks;
};

/**
 * Analyse audio files for waveform data
 */
export const getWaveformData = async (
  file: File
): Promise<WaveFormInput | null> => {
  const audioContext = getOrCreateWaveformAudioContext();
  if (!audioContext) {
    return null;
  }

  clearWaveformAudioContextIdleTimer();

  try {
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const duration = Number(audioBuffer.duration.toFixed(2));
    const controlPeakCount = 1000;

    const leftChannel = audioBuffer.getChannelData(0);
    const rightChannel =
      audioBuffer.numberOfChannels > 1
        ? audioBuffer.getChannelData(1)
        : leftChannel;

    const leftPeaks = calcPeaks(leftChannel, controlPeakCount); // [min,max,...]
    const rightPeaks = calcPeaks(rightChannel, controlPeakCount); // [min,max,...]

    // Flatten as: [...leftPeaks, ...rightPeaks]
    const flattenedPeaks: number[] = [];
    for (const peak of leftPeaks) {
      flattenedPeaks.push(peak);
    }

    for (const peak of rightPeaks) {
      flattenedPeaks.push(peak);
    }

    const peaks = encodeWaveformPeaks(flattenedPeaks);
    return {
      duration,
      peakCount: controlPeakCount,
      peaks,
    };
  } catch (error) {
    console.warn(
      '[getWaveformData] Unable to generate waveform. Upload will continue without waveform data.',
      { fileName: file.name, error }
    );
    return null;
  } finally {
    scheduleWaveformAudioContextClose();
  }
};

// Encode waveform peak data into base64 string for API transfer
export const encodeWaveformPeaks = (peaks: number[]): string => {
  const float32 = new Float32Array(peaks);
  const bytes = new Uint8Array(float32.buffer);

  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
};

// Decode base64 string into number[] for waveform render
export const decodeWaveformPeaks = (peaks: string): number[][] => {
  const binary = atob(peaks);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  if (bytes.byteLength % 4 !== 0) {
    throw new Error(
      `Invalid waveform data length (${bytes.byteLength}); expected a multiple of 4 bytes`
    );
  }

  const float32 = new Float32Array(
    bytes.buffer,
    bytes.byteOffset,
    bytes.byteLength / 4
  );

  const flatPeaks = Array.from(float32);
  if (flatPeaks.length % 2 !== 0) {
    throw new Error(
      `Invalid flattened stereo peak length (${flatPeaks.length}); expected even length`
    );
  }
  const half = flatPeaks.length / 2;
  const leftPeaks: number[] = [];
  const rightPeaks: number[] = [];
  for (let i = 0; i < half; i++) {
    leftPeaks.push(flatPeaks[i]);
  }
  for (let i = half; i < flatPeaks.length; i++) {
    rightPeaks.push(flatPeaks[i]);
  }
  return [leftPeaks, rightPeaks];
};
